import React, { useRef, useEffect, useCallback } from 'react';

interface FormattedTextareaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  error?: string;
  rows?: number;
  readOnly?: boolean;
  'data-tour'?: string;
}

export const FormattedTextarea: React.FC<FormattedTextareaProps> = ({
  value,
  onChange,
  placeholder,
  required,
  error,
  rows = 4,
  readOnly = false,
  'data-tour': dataTour,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);

  const updateContent = useCallback(() => {
    if (editorRef.current) {
      const content = editorRef.current.innerText;
      onChange(content);
    }
  }, [onChange]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      // Store current cursor position
      const selection = window.getSelection();
      const range = selection?.getRangeAt(0);
      let cursorPosition = 0;

      if (range) {
        const preSelectionRange = range.cloneRange();
        preSelectionRange.selectNodeContents(editorRef.current);
        preSelectionRange.setEnd(range.startContainer, range.startOffset);
        cursorPosition = preSelectionRange.toString().length;
      }

      // Get and update content
      const content = editorRef.current.innerText;
      if (content === placeholder) {
        onChange('');
      } else {
        onChange(content);
      }

      // Restore cursor position after React updates the content
      requestAnimationFrame(() => {
        if (!editorRef.current) return;

        const textNodes: Node[] = [];
        const walk = document.createTreeWalker(editorRef.current, NodeFilter.SHOW_TEXT, null);

        let node: Node | null;
        while ((node = walk.nextNode())) {
          textNodes.push(node);
        }

        let currentPos = 0;
        let targetNode: Node | null = null;
        let targetOffset = 0;

        // Find the correct text node and offset
        for (const node of textNodes) {
          const nodeLength = node.textContent?.length || 0;
          if (currentPos + nodeLength >= cursorPosition) {
            targetNode = node;
            targetOffset = cursorPosition - currentPos;
            break;
          }
          currentPos += nodeLength;
        }

        if (targetNode && selection) {
          const newRange = document.createRange();
          newRange.setStart(targetNode, targetOffset);
          newRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(newRange);
        }
      });
    }
  }, [onChange, placeholder]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  }, []);

  const handleFocus = useCallback(() => {
    if (editorRef.current && !value && editorRef.current.innerText === placeholder) {
      editorRef.current.innerText = '';
      editorRef.current.classList.remove('text-gray-400');
    }
  }, [value, placeholder]);

  const handleBlur = useCallback(() => {
    if (editorRef.current && !value) {
      editorRef.current.innerText = placeholder || '';
      editorRef.current.classList.add('text-gray-400');
    }
  }, [value, placeholder]);

  useEffect(() => {
    if (editorRef.current) {
      // Skip when the editor already matches the parent value — the user is
      // mid-keystroke. handleInput already updated the DOM and called onChange;
      // rewriting innerHTML here would wipe text nodes and reset the cursor.
      if (editorRef.current.innerText === value) {
        return;
      }

      // Always set the content first
      if (!value) {
        editorRef.current.innerText = placeholder || '';
        editorRef.current.classList.add('text-gray-400');
        return;
      }

      editorRef.current.classList.remove('text-gray-400');
      const parts = value.split(/(\{[^}]+\})/g);
      editorRef.current.innerHTML = parts
        .map((part) => {
          if (part.startsWith('{') && part.endsWith('}')) {
            return `<strong>${part}</strong>`;
          }
          return part;
        })
        .join('');

      // Try to handle selection, but don't let selection errors prevent content update
      try {
        const selection = window.getSelection();
        const range = selection?.getRangeAt(0);
        let cursorPosition = 0;

        // Calculate cursor position in plain text before updating content
        if (range) {
          const preSelectionRange = range.cloneRange();
          preSelectionRange.selectNodeContents(editorRef.current);
          preSelectionRange.setEnd(range.startContainer, range.startOffset);
          cursorPosition = preSelectionRange.toString().length;

          // Restore cursor position
          const textNodes: Node[] = [];
          const walk = document.createTreeWalker(editorRef.current, NodeFilter.SHOW_TEXT, null);

          let node: Node | null;
          while ((node = walk.nextNode())) {
            textNodes.push(node);
          }

          let currentPos = 0;
          let targetNode: Node | null = null;
          let targetOffset = 0;

          // Find the correct text node and offset
          for (const node of textNodes) {
            const nodeLength = node.textContent?.length || 0;
            if (currentPos + nodeLength >= cursorPosition) {
              targetNode = node;
              targetOffset = cursorPosition - currentPos;
              break;
            }
            currentPos += nodeLength;
          }

          if (targetNode && selection) {
            const newRange = document.createRange();
            newRange.setStart(targetNode, targetOffset);
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);
          }
        }
      } catch (error) {
        // Log selection errors but don't let them affect content display
        console.debug('Selection error:', error);
      }
    }
  }, [value, placeholder]);

  return (
    <div className="relative" data-tour={dataTour}>
      <div
        ref={editorRef}
        contentEditable={!readOnly}
        className={`shadow-sm text-sm min-h-[96px] max-h-[200px] scrollbar-custom overflow-y-auto px-3 py-2 border rounded-md border-foreground/30 bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring whitespace-pre-wrap ${
          readOnly ? 'cursor-not-allowed bg-muted text-muted-foreground' : ''
        } ${error ? '!border-red-500' : ''}`}
        onInput={handleInput}
        onPaste={handlePaste}
        onFocus={handleFocus}
        onBlur={handleBlur}
        style={{ lineHeight: '1.5' }}
      />
      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
};
